/*
 * "What people have to fill in" — the registration form's field configuration.
 *
 * ONE configuration, THREE ways in. Whatever an organizer ticks here is what the
 * shareable /r/<id> link renders, what an external website posting through the API
 * is allowed to send, and what the Register button inside the app asks for. The
 * server reads it back through buildFormSchema() in
 * functions/lib/registration-api.js, so this is not a hint to a form somewhere —
 * it is the rule a submission is validated against.
 *
 * WHERE IT OPENS FROM. Two places, deliberately:
 *   - the tournament's registration options (Create as well as Options), because
 *     "what do I need to know about the people coming?" is a question you answer
 *     while setting the tournament up, not one you go hunting for afterwards;
 *   - the registration link / API modal, which is where an organizer already is
 *     when they think about the form an outside website will show.
 *
 * WHERE IT SAVES TO. `registrationApi.fields` and `registrationApi.customFields`
 * on the tournament — and ONLY those two. The same document holds the link and API
 * on/off switches and the access code, which belong to the other modal; merging
 * rather than overwriting is what lets the two be edited in either order without
 * one silently undoing the other. While CREATING, there is no tournament document
 * yet, so the choices are staged in state.draftRegistrationApi and committed by the
 * create form's submit handler.
 */

import { state, saveToLocalStorage } from '../state.js';
import { escapeHtml } from '../utils/formatting.js';
import { isDraftId } from '../utils/tournament-form-logic.js';

// MUST stay in step with FIELD_CATALOG in functions/lib/registration-api.js — that
// copy is the one that validates submissions; this one only draws the picker.
// tests/registration-field-catalog.test.js fails if the two drift apart.
export const FIELD_CATALOG = [
    { key: 'givenName', label: 'Given name', locked: true },
    { key: 'lastName', label: 'Last name', locked: true },
    { key: 'email', label: 'Email', hint: 'Where the confirmation and the cancel link are sent' },
    { key: 'phone', label: 'Phone' },
    { key: 'club', label: 'Club' },
    { key: 'fed', label: 'Federation', hint: '3-letter code, e.g. NOR' },
    { key: 'fideId', label: 'FIDE ID' },
    { key: 'nsfId', label: 'National ID' },
    // Two fields, not one, because they answer different questions. An age class
    // counted by year of birth ("born 2014 or later") needs nothing more than the
    // year, and asking a nine-year-old's parent for the exact date collects
    // personal data the tournament will never use. An event where eligibility is
    // decided by how old someone is on a given day — or one reporting to FIDE,
    // which wants the full date — needs the date.
    { key: 'birthYear', label: 'Year of birth', hint: 'Enough for age classes counted by year — the usual choice for a children\'s tournament' },
    { key: 'birthDate', label: 'Date of birth', hint: 'The full date, for classes decided by age on a given day and for FIDE reporting' },
    { key: 'sex', label: 'Sex' },
    { key: 'title', label: 'FIDE title' },
    { key: 'rating', label: 'Rating', hint: 'Stored as the standard rating' },
    { key: 'rapidRating', label: 'Rapid rating' },
    { key: 'blitzRating', label: 'Blitz rating' },
    { key: 'comment', label: 'Comment' },
    // Only meaningful for a team tournament, where the Cloud Function forces it on
    // regardless of what is chosen here (see appFieldConfig in registration-api.js).
    { key: 'teamName', label: 'Team', hint: 'Which team the entry is for — the first entry naming a team creates it' }
];

// Same defaults the Cloud Function applies to a tournament that was never configured.
export const FIELD_DEFAULTS = {
    givenName: 'required', lastName: 'required', email: 'required',
    club: 'optional', fed: 'optional', fideId: 'optional',
    phone: 'off', nsfId: 'off', birthYear: 'off', birthDate: 'off',
    sex: 'off', title: 'off', rating: 'off', rapidRating: 'off', blitzRating: 'off',
    comment: 'off', teamName: 'off'
};

export const CUSTOM_TYPES = ['text', 'textarea', 'number', 'select', 'checkbox'];

const REQUIREMENTS = ['off', 'optional', 'required'];

// Working copies; committed to the tournament on Save.
let draftFields = {};
let draftCustom = [];
// Called after a successful save, so whoever opened this can redraw its summary.
let onSaved = null;

const $ = (id) => document.getElementById(id);

function modal() { return $('registration-fields-modal'); }

// ---------------------------------------------------------------- shared state

/*
 * Is the tournament form underneath CREATING a tournament right now?
 *
 * Read off the form's own mode flag — the one its submit handler reads — rather
 * than off state, because state does not know: while the create form is open,
 * state.activeTournament is still whatever the organizer had open BEFORE pressing
 * "Create New Tournament". (The groups editor replaces it with a draft, but a
 * single-group tournament never gets one.)
 */
function isCreating() {
    const form = document.getElementById('create-tournament-modal');
    return !!(form && form.classList.contains('active') && form.dataset.mode !== 'edit');
}

/*
 * The tournament these panels configure. Never null, so no render has to guard.
 *
 * While CREATING, only the create form's own draft counts. The previously open
 * tournament is still sitting in state.activeTournament, and treating it as the
 * one being configured is exactly wrong: the link panel would hand out ITS
 * registration link and QR code, and "what people have to fill in" would read
 * ITS questions and write the new ones back onto IT — while the tournament being
 * created ends up with the defaults, every field the organizer just made required
 * showing up as optional. Draft-ness is decided by the id — see isDraftMode().
 */
export function currentTournament() {
    const t = state.activeTournament;
    if (!t) return {};
    if (isCreating() && !isDraftId(t.id)) return {};
    return t;
}

// True while the tournament being configured has not been saved yet (the panel was
// opened from the CREATE form).
export function isDraftMode() {
    const t = currentTournament();
    return !(t.id && !isDraftId(t.id));
}

/** The registration configuration in force right now — staged one while creating. */
export function readRegistrationConfig() {
    const t = currentTournament();
    return (isDraftMode() ? state.draftRegistrationApi : (t && t.registrationApi)) || {};
}

/** The requirement for one field, with the tournament's defaults filled in. */
function requirementOf(cfg, key) {
    const raw = (cfg.fields || {})[key];
    return REQUIREMENTS.includes(raw) ? raw : FIELD_DEFAULTS[key];
}

/*
 * The tournament-wide "Require Phone Number" toggle, read from the edit form when
 * it is on screen. The saved flag is one Save behind whatever the organizer just
 * ticked in the modal underneath, and the picker showing the stale answer would be
 * the more confusing of the two wrongs.
 */
function phoneForced() {
    const toggle = $('tourney-require-phone');
    return toggle ? toggle.checked : !!currentTournament().requirePhone;
}

/**
 * One line describing what the form currently asks for, for the buttons that open
 * this modal. Kept short on purpose — it is a reassurance, not a report.
 */
export function summarizeRegistrationFields() {
    const cfg = readRegistrationConfig();
    let asked = 0;
    let required = 0;
    FIELD_CATALOG.forEach(f => {
        const req = f.locked ? 'required' : requirementOf(cfg, f.key);
        if (req === 'off') return;
        asked++;
        if (req === 'required') required++;
    });
    const custom = Array.isArray(cfg.customFields) ? cfg.customFields.length : 0;

    let text = tr('Asks for {n} details, {r} of them required.', { n: asked, r: required });
    if (custom > 0) {
        text += ' ' + tr(custom === 1 ? '1 extra question of your own.' : '{n} extra questions of your own.', { n: custom });
    }
    return text;
}

/** Refresh every "what people have to fill in" summary currently on screen. */
export function renderRegistrationFieldsSummaries() {
    const text = summarizeRegistrationFields();
    ['registration-fields-summary', 'reg-api-fields-summary'].forEach(id => {
        const el = $(id);
        if (el) el.textContent = text;
    });
}

// ---------------------------------------------------------------- open/close

export function openRegistrationFieldsModal(options) {
    const opts = options || {};
    onSaved = typeof opts.onSaved === 'function' ? opts.onSaved : null;

    const cfg = readRegistrationConfig();
    draftFields = Object.assign({}, FIELD_DEFAULTS, cfg.fields || {});
    draftCustom = Array.isArray(cfg.customFields)
        ? cfg.customFields.map(f => ({
            key: String(f.key || ''),
            label: String(f.label || ''),
            type: CUSTOM_TYPES.includes(f.type) ? f.type : 'text',
            options: Array.isArray(f.options) ? f.options.slice() : [],
            requirement: REQUIREMENTS.includes(f.requirement) ? f.requirement : 'optional',
            // A question people have already answered keeps its key: it is what
            // their stored answers are filed under, and what an external form posts.
            locked: !!f.key
        }))
        : [];

    renderAll();
    modal().classList.add('active');
}

export function closeRegistrationFieldsModal() {
    modal().classList.remove('active');
    onSaved = null;
}

// ------------------------------------------------------------------ renders

function renderAll() {
    const t = currentTournament();
    // While creating, the name box of the form underneath is the only name there is.
    const nameInput = $('tourney-name');
    const nameEl = $('reg-fields-tournament-name');
    if (nameEl) {
        nameEl.textContent =
            (t && t.name) || (nameInput && nameInput.value.trim()) || tr('this tournament');
    }
    renderFieldRows();
    renderCustomRows();
}

function requirementSelect(id, value, disabled) {
    const opt = (v, label) =>
        `<option value="${v}"${value === v ? ' selected' : ''}>${escapeHtml(tr(label))}</option>`;
    return `<select class="form-control reg-fields-req" id="${escapeHtml(id)}" style="padding: 0.35rem 0.5rem; font-size: 0.85rem;"${disabled ? ' disabled' : ''}>
        ${opt('off', 'Do not ask')}
        ${opt('optional', 'Optional')}
        ${opt('required', 'Required')}
    </select>`;
}

function renderFieldRows() {
    const host = $('reg-fields-list');
    if (!host) return;
    const forcedPhone = phoneForced();

    host.innerHTML = FIELD_CATALOG.map(f => {
        const locked = f.locked;
        // The tournament-wide "Require Phone Number" toggle can only tighten this.
        const forced = f.key === 'phone' && forcedPhone;
        const value = locked ? 'required' : (forced ? 'required' : (draftFields[f.key] || 'off'));
        const note = locked
            ? tr('Always required')
            : (forced ? tr('Forced by "Require Phone Number"') : (f.hint ? tr(f.hint) : ''));
        return `
            <div style="display: flex; align-items: center; gap: 0.75rem; padding: 0.4rem 0;">
                <div style="flex: 1; min-width: 0;">
                    <div style="font-size: 0.9rem; color: var(--text-main);">${escapeHtml(tr(f.label))}</div>
                    ${note ? `<div style="font-size: 0.72rem; color: var(--text-muted);">${escapeHtml(note)}</div>` : ''}
                    <div style="font-size: 0.68rem; color: var(--text-muted); font-family: monospace;">${escapeHtml(f.key)}</div>
                </div>
                <div style="width: 9.5rem; flex-shrink: 0;">
                    ${requirementSelect('reg-fields-field-' + f.key, value, locked || forced)}
                </div>
            </div>`;
    }).join('');

    FIELD_CATALOG.forEach(f => {
        const el = $('reg-fields-field-' + f.key);
        if (el && !el.disabled) {
            el.addEventListener('change', () => { draftFields[f.key] = el.value; });
        }
    });
}

function renderCustomRows() {
    const host = $('reg-fields-custom');
    if (!host) return;
    if (draftCustom.length === 0) {
        host.innerHTML = `<p style="font-size: 0.8rem; color: var(--text-muted); margin: 0;">${escapeHtml(tr('No extra questions yet.'))}</p>`;
        return;
    }
    host.innerHTML = draftCustom.map((f, i) => `
        <div style="border: 1px solid var(--border-color); border-radius: 8px; padding: 0.6rem; display: flex; flex-direction: column; gap: 0.5rem;">
            <div style="display: flex; gap: 0.5rem; align-items: center;">
                <input type="text" class="form-control" id="reg-fields-custom-label-${i}" value="${escapeHtml(f.label)}" placeholder="${escapeHtml(tr('Question'))}" style="flex: 2; font-size: 0.85rem;">
                <select class="form-control" id="reg-fields-custom-type-${i}" style="flex: 1; font-size: 0.85rem;">
                    ${CUSTOM_TYPES.map(ty => `<option value="${ty}"${f.type === ty ? ' selected' : ''}>${escapeHtml(tr(ty))}</option>`).join('')}
                </select>
                <div style="width: 8.5rem;">${requirementSelect('reg-fields-custom-req-' + i, f.requirement, false)}</div>
                <button type="button" class="btn-icon-small btn-icon-danger reg-fields-custom-remove" data-index="${i}" title="${escapeHtml(tr('Remove'))}"><i class="ri-delete-bin-line"></i></button>
            </div>
            <div style="display: flex; gap: 0.5rem; align-items: center;">
                <span style="font-size: 0.72rem; color: var(--text-muted); font-family: monospace; white-space: nowrap;">${escapeHtml(f.key || '—')}</span>
                <input type="text" class="form-control" id="reg-fields-custom-options-${i}" value="${escapeHtml(f.options.join(', '))}"
                       placeholder="${escapeHtml(tr('Choices, comma separated (only for a dropdown)'))}"
                       style="flex: 1; font-size: 0.8rem;${f.type === 'select' ? '' : ' display: none;'}">
            </div>
        </div>`).join('');

    draftCustom.forEach((f, i) => {
        const label = $(`reg-fields-custom-label-${i}`);
        const type = $(`reg-fields-custom-type-${i}`);
        const req = $(`reg-fields-custom-req-${i}`);
        const options = $(`reg-fields-custom-options-${i}`);
        if (label) label.addEventListener('input', () => {
            f.label = label.value;
            // The key is what answers are stored under and what an external form
            // posts; derive it from the label, but never change it once the
            // question has been saved, or existing forms and answers break.
            if (!f.locked) {
                f.key = slugify(label.value);
                const keyEl = $(`reg-fields-custom-options-${i}`);
                if (keyEl && keyEl.previousElementSibling) {
                    keyEl.previousElementSibling.textContent = f.key || '—';
                }
            }
        });
        if (type) type.addEventListener('change', () => {
            f.type = type.value;
            renderCustomRows();
        });
        if (req) req.addEventListener('change', () => { f.requirement = req.value; });
        if (options) options.addEventListener('input', () => {
            f.options = options.value.split(',').map(s => s.trim()).filter(Boolean);
        });
    });

    host.querySelectorAll('.reg-fields-custom-remove').forEach(btn => {
        btn.addEventListener('click', () => {
            draftCustom.splice(parseInt(btn.dataset.index, 10), 1);
            renderCustomRows();
        });
    });
}

function slugify(s) {
    return String(s || '').toLowerCase()
        .replace(/[æå]/g, 'a').replace(/ø/g, 'o')
        .replace(/[^a-z0-9]+/g, '')
        .slice(0, 24);
}

// ------------------------------------------------------------------- saving

function saveFields() {
    // Drop questions the organizer left unnamed, and selects with no choices —
    // the Cloud Function would discard them anyway.
    const customFields = draftCustom
        .filter(f => f.key && f.label)
        .filter(f => f.type !== 'select' || f.options.length > 0)
        .map(f => ({
            key: f.key,
            label: f.label,
            type: f.type,
            options: f.options,
            requirement: f.requirement
        }));

    // MERGE, never replace: the link/API switches and the access code live on the
    // same object and are none of this modal's business.
    const config = Object.assign({}, readRegistrationConfig(), {
        fields: Object.assign({}, draftFields),
        customFields
    });

    // Keep the legacy toggle honest: `requirePhone` is what the app's own form and
    // the group settings read, so a phone requirement set here has to show up there.
    const requirePhone = draftFields.phone === 'required';
    const phoneToggle = $('tourney-require-phone');
    if (phoneToggle) phoneToggle.checked = requirePhone;

    if (isDraftMode()) {
        // No tournament document yet: stage it, and let the create form's submit
        // handler attach it to the tournament it builds.
        state.draftRegistrationApi = config;
        finishSave(tr('Saved with the tournament when you create it.'));
        return;
    }

    const t = currentTournament();
    if (!t || !t.id) return;
    t.registrationApi = config;
    t.requirePhone = requirePhone;

    saveToLocalStorage();
    if (window.authApp && window.authApp.scheduleSave) window.authApp.scheduleSave(state);

    finishSave(tr('Registration form saved.'));
}

function finishSave(message) {
    const done = onSaved;
    closeRegistrationFieldsModal();
    renderRegistrationFieldsSummaries();
    if (done) done();
    window.Dialog.success(message, tr('Saved'));
}

// -------------------------------------------------------------------- wiring

export function initRegistrationFieldsUI() {
    const openBtn = $('btn-registration-fields');
    if (openBtn) openBtn.addEventListener('click', () => openRegistrationFieldsModal());

    const m = modal();
    if (!m) return;

    m.querySelectorAll('.btn-close-modal').forEach(btn => {
        btn.addEventListener('click', closeRegistrationFieldsModal);
    });
    m.addEventListener('click', (e) => { if (e.target === m) closeRegistrationFieldsModal(); });

    $('btn-reg-fields-save').addEventListener('click', saveFields);
    $('btn-reg-fields-add-custom').addEventListener('click', () => {
        draftCustom.push({ key: '', label: '', type: 'text', options: [], requirement: 'optional', locked: false });
        renderCustomRows();
    });
}
